import { useCallback, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import uuidV4 from 'lib/string/uuidV4Utils'
import type { ChatMessageAttachment } from 'tripPlanner/api'
import { createChat, createMessageStream, fileToChatMessageAttachment, stopMessageStream } from 'tripPlanner/api'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import { isAbortError } from 'api/requestUtils'
import { nonNullable } from 'lib/array/arrayUtils'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import type { StreamMachineEvent } from './useStreamMachine'
import type { StreamHandlers } from './useStreamEventProcessor'
import { isNetworkError, getStreamErrorDisplayMessage, delay, MAX_RETRIES, RETRY_DELAYS } from 'tripPlanner/utils/networkError'
import {
  trackStreamStart,
  trackStreamComplete,
  trackStreamError,
  trackRetry,
} from 'tripPlanner/utils/streamAnalytics'
import { getTextFromParts } from 'tripPlanner/utils/messageParts'
import type { UserData } from '../types'

interface StreamConfig {
  chatId: number
  content: string
  userMessageId: string
  assistantMessageId: string
  regenerateMessageId?: string
  attachments?: Array<ChatMessageAttachment>
  /** Whether retries are safe for this request. Only true for idempotent submit requests. */
  retryable: boolean
}

interface UseStreamConnectionArgs {
  // Machine
  send: (event: StreamMachineEvent) => void
  // Message overlay
  setPendingUserMessage: (message: MessageItem | null) => void
  setStreamingMessage: (message: MessageItem | null) => void
  invalidateChat: (forChatId?: number) => Promise<{ messagesWithContent: Set<string>; skipped: boolean }>
  // Event processor
  createHandlers: (generation: number) => StreamHandlers
  resetBuffer: () => void
  // Scroll
  scrollToNewMessage: (messageId: string) => void
  recalculateSpacerAfterStream: () => void
  // External callbacks
  refetchChatList: () => Promise<unknown>
  startChatTitleGeneration: (chatId: number, message: string) => void
  updateChatLastActivityTimestamp: (chatId: number, iso: string) => void
  onStreamComplete?: (chatId: number) => void
  // Config
  chatId: number | undefined
  setChatId: (id: number | undefined) => void
  prompt: string | undefined
  isLoggedIn: boolean
  isStreaming: boolean
  orderIds?: Array<string>
  offerIds?: Array<string>
  files: Array<File>
  setFiles: (files: Array<File>) => void
  userData?: UserData
  messages: Array<MessageItem>
  latestGenerationRef: React.MutableRefObject<number>
  // Streaming message ID ref (shared with event processor)
  streamingMessageIdRef: React.MutableRefObject<string | null>
}

export interface UseStreamConnectionReturn {
  handleSubmit: (message: string, options?: { messageType?: string }) => Promise<void>
  handleRegenerateResponse: () => Promise<void>
  handleStopStreaming: () => Promise<boolean>
  handleRetry: (config: { chatId: number; userMessageId: string; assistantMessageId: string; content: string }) => Promise<void>
  resetStreamingState: () => void
}

// ── Hook ───────────────────────────────────────────────────────────────

export function useStreamConnection({
  send,
  setPendingUserMessage,
  setStreamingMessage,
  invalidateChat,
  createHandlers,
  resetBuffer,
  scrollToNewMessage,
  recalculateSpacerAfterStream,
  refetchChatList,
  startChatTitleGeneration,
  updateChatLastActivityTimestamp,
  onStreamComplete,
  chatId,
  setChatId,
  prompt,
  isLoggedIn,
  isStreaming,
  orderIds,
  offerIds,
  files,
  setFiles,
  userData,
  messages,
  latestGenerationRef,
  streamingMessageIdRef,
}: UseStreamConnectionArgs): UseStreamConnectionReturn {
  const intl = useIntl()
  const accessToken = useAppSelector(state => state.auth.accessToken)
  const streamAbortControllerRef = useRef<AbortController | null>(null)
  const currentStreamingGenerationRef = useRef<number>(0)

  const defaultErrorMessage = intl.formatMessage({
    id: 'EmZPR-KxU7oRFczGeJk2',
    defaultMessage: 'Something went wrong. Please try again.',
    description: 'Default error message when a chat streaming request fails',
  })
  const networkErrorMessage = intl.formatMessage({
    id: 'zru2DO-wO2v0Kg1nYQ15',
    defaultMessage: 'Unable to connect. Please check your internet connection and try again.',
    description: 'Error message when chat fails due to network connectivity issues',
  })

  // Abort on unmount
  useEffect(() => {
    return () => {
      if (streamAbortControllerRef.current) {
        streamAbortControllerRef.current.abort()
        streamAbortControllerRef.current = null
      }
    }
  }, [])

  // ── Shared stream execution ──

  const executeStream = useCallback(async(
    config: StreamConfig,
    thisGeneration: number,
    handlers: StreamHandlers,
  ) => {
    const { chatId: streamChatId, content, userMessageId, assistantMessageId, regenerateMessageId, attachments, retryable } = config

    // Create streaming message overlay
    setStreamingMessage({
      id: assistantMessageId,
      parts: [],
      role: 'assistant',
      rawContent: '',
      messageType: 'message',
    })
    streamingMessageIdRef.current = assistantMessageId
    resetBuffer()

    const abortController = new AbortController()
    streamAbortControllerRef.current?.abort()
    streamAbortControllerRef.current = abortController

    send({
      type: 'CONNECTED',
      chatId: streamChatId,
      userMessageId,
      assistantMessageId,
      abortController,
    })
    trackStreamStart(streamChatId)

    // Build request payload — regeneration skips assistantMessageId
    // (server ignores it for regen; only uses regenerateMessageId + userMessageId)
    const requestPayload = regenerateMessageId
      ? {
          chatId: streamChatId,
          content,
          regenerateMessageId,
          userMessageId,
          ...(prompt ? { prompt } : {}),
          ...(userData && { userData }),
        }
      : {
          chatId: streamChatId,
          content,
          userMessageId,
          assistantMessageId,
          ...(prompt ? { prompt } : {}),
          ...(attachments && attachments.length > 0 ? { files: attachments } : {}),
          ...(userData && { userData }),
        }

    let capturedError: unknown

    try {
      // Retry loop for transient network errors.
      // Only retryable for submit (server idempotency via userMessageId).
      // Regeneration skips idempotency — retry could cause duplicate OpenAI calls.
      const maxAttempts = retryable ? MAX_RETRIES : 0
      for (let attempt = 0; attempt <= maxAttempts; attempt++) {
        try {
          await createMessageStream(
            requestPayload,
            accessToken ?? undefined,
            (chunk: string) => {
              handlers.processJsonlChunk(chunk, streamChatId)
            },
            abortController.signal,
          )
          break // success — exit retry loop
        } catch(error) {
          if (isAbortError(error)) break // user stop — not an error
          if (!retryable || !isNetworkError(error) || attempt === maxAttempts) throw error

          // Network error — retry with same IDs (server idempotency)
          const nextAttempt = attempt + 1
          send({ type: 'RETRY', attempt: nextAttempt })
          trackRetry(streamChatId, nextAttempt)
          resetBuffer()
          await delay(RETRY_DELAYS[attempt], abortController.signal)
        }
      }
    } catch(streamError) {
      if (!isAbortError(streamError)) {
        capturedError = streamError
        throw streamError
      }
    } finally {
      streamAbortControllerRef.current = null

      // If the stream was aborted (user stop or stale generation), skip full
      // cleanup — handleStopStreaming already handled UI state, and the server
      // may have partially-processed content that shouldn't be fetched yet.
      const wasAborted = abortController.signal.aborted

      // Always cancel pending RAFs regardless of how the stream ended
      handlers.dispose()

      const isCurrentGeneration = thisGeneration >= latestGenerationRef.current
      const isError = capturedError !== undefined

      if (isCurrentGeneration && !wasAborted) {
        handlers.flushTrailingBuffer(streamChatId)

        if (!isError) {
          onStreamComplete?.(streamChatId)
          trackStreamComplete(streamChatId, false)
        }

        const { messagesWithContent } = await invalidateChat(streamChatId)

        if (isError || messagesWithContent.has(assistantMessageId)) {
          setStreamingMessage(null)
        }
        if (messagesWithContent.has(userMessageId)) {
          setPendingUserMessage(null)
        }

        recalculateSpacerAfterStream()
        streamingMessageIdRef.current = null
      }

      if (isError) {
        // Machine enters error state — preserves context (chatId, messageIds)
        // for potential inline retry. Streaming overlay may still be visible
        // with partial content if the server didn't persist it yet.
        send({
          type: 'ERROR',
          error: capturedError instanceof Error ? capturedError : new Error(String(capturedError)),
        })
      } else {
        // Always reset machine to idle — even for stale generations — so subsequent
        // SUBMIT/REGENERATE events are not silently dropped.
        send({ type: 'STREAM_END' })
      }
    }
  }, [
    accessToken,
    prompt,
    userData,
    latestGenerationRef,
    send,
    setStreamingMessage,
    setPendingUserMessage,
    invalidateChat,
    resetBuffer,
    recalculateSpacerAfterStream,
    onStreamComplete,
    streamingMessageIdRef,
  ])

  // ── Ensure chat exists ──

  const ensureChatId = useCallback(async(message?: string): Promise<number> => {
    if (chatId !== undefined) return chatId

    const chatResponse = await createChat({
      ...(orderIds?.length ? { orderIds } : {}),
      ...(offerIds?.length ? { offerIds } : {}),
    })
    const newChatId = chatResponse.id
    setChatId(newChatId)

    if (isLoggedIn) {
      await refetchChatList()
    }
    if (message) {
      startChatTitleGeneration(newChatId, message)
    }

    return newChatId
  }, [chatId, orderIds, offerIds, setChatId, isLoggedIn, refetchChatList, startChatTitleGeneration])

  // ── Submit ──

  const handleSubmit = useCallback(async(message: string, options?: { messageType?: string }) => {
    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'SUBMIT', generation: thisGeneration })

    const userMessageId = uuidV4()
    const userMessage: MessageItem = {
      id: userMessageId,
      parts: [{ type: 'text', content: message }],
      role: 'user',
      files: [...files],
      rawContent: message,
      messageType: options?.messageType ?? 'message',
    }

    setPendingUserMessage(userMessage)
    scrollToNewMessage(userMessageId)

    const currentFiles = [...files]
    setFiles([])

    let currentChatId: number | undefined = chatId
    let executeStreamCalled = false
    try {
      currentChatId = await ensureChatId(message)
      updateChatLastActivityTimestamp(currentChatId, new Date().toISOString())

      const assistantMessageId = uuidV4()
      const handlers = createHandlers(thisGeneration)

      // Encode file attachments
      let attachments: Array<ChatMessageAttachment> = []
      if (currentFiles.length > 0) {
        const failedFileNames: Array<string> = []
        const encodedFiles = await Promise.all(
          currentFiles.map(async(file) => {
            try {
              return await fileToChatMessageAttachment(file)
            } catch {
              failedFileNames.push(file.name)
              return undefined
            }
          }),
        )
        attachments = nonNullable(encodedFiles)
        if (failedFileNames.length > 0) {
          showSnackbar(
            intl.formatMessage(
              {
                id: 'aoIXiW41sPchU8OjeWiY',
                defaultMessage: '{count, plural, one {# file} other {# files}} could not be attached: {names}',
                description: 'Warning when one or more files fail to encode for chat attachment',
              },
              { count: failedFileNames.length, names: failedFileNames.join(', ') },
            ),
            'warning',
          )
        }
      }

      executeStreamCalled = true
      await executeStream(
        { chatId: currentChatId, content: message, userMessageId, assistantMessageId, attachments, retryable: true },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      if (!executeStreamCalled) {
        // Pre-stream error (createChat, file encoding) — clean up overlays
        setPendingUserMessage(null)
        setStreamingMessage(null)
        send({
          type: 'ERROR',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }
      // If executeStreamCalled, executeStream's finally block already handled
      // machine events, invalidation, and conditional overlay cleanup.

      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(currentChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'nDA42QsZVeANpf0KNZxr',
          defaultMessage: 'Message failed',
          description: 'Snackbar heading when sending a chat message fails',
        }),
      })
      throw error
    }
  }, [
    latestGenerationRef,
    send,
    files,
    setPendingUserMessage,
    scrollToNewMessage,
    setFiles,
    ensureChatId,
    updateChatLastActivityTimestamp,
    createHandlers,
    executeStream,
    setStreamingMessage,
    chatId,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Regenerate ──

  const handleRegenerateResponse = useCallback(async() => {
    const reversed = [...messages].reverse()
    const lastUserMessage = reversed.find((m) => m.role === 'user')
    if (!lastUserMessage) return
    const assistantMessageToRegenerate = reversed.find((m) => m.role === 'assistant')
    if (!assistantMessageToRegenerate) return

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'REGENERATE', generation: thisGeneration })

    // For regeneration, reuse the existing assistant message ID — ID-based
    // deduplication filters out the old server message so the streaming
    // message visually "replaces" it immediately
    const regenerateAssistantId = assistantMessageToRegenerate.id
    const regenerateUserId = lastUserMessage.id

    setStreamingMessage(null)

    let currentChatId: number | undefined = chatId
    let executeStreamCalled = false
    try {
      currentChatId = await ensureChatId()

      const handlers = createHandlers(thisGeneration)

      executeStreamCalled = true
      await executeStream(
        {
          chatId: currentChatId,
          content: getTextFromParts(lastUserMessage.parts),
          userMessageId: regenerateUserId,
          assistantMessageId: regenerateAssistantId,
          regenerateMessageId: regenerateAssistantId,
          retryable: false, // Regeneration skips server idempotency — retry unsafe
        },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      if (!executeStreamCalled) {
        setStreamingMessage(null)
        send({
          type: 'ERROR',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      }

      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(currentChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'pp-WwVLFlIsUCNNe3QOD',
          defaultMessage: 'Regeneration failed',
          description: 'Snackbar heading when regenerating a chat response fails',
        }),
      })
      throw error
    }
  }, [
    messages,
    latestGenerationRef,
    send,
    setStreamingMessage,
    ensureChatId,
    createHandlers,
    executeStream,
    chatId,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Retry (G4: inline retry with same IDs for server idempotency) ──

  const handleRetry = useCallback(async(config: {
    chatId: number
    userMessageId: string
    assistantMessageId: string
    content: string
  }) => {
    const { chatId: retryChatId, userMessageId, assistantMessageId, content } = config

    const thisGeneration = latestGenerationRef.current + 1
    latestGenerationRef.current = thisGeneration
    currentStreamingGenerationRef.current = thisGeneration

    send({ type: 'SUBMIT', generation: thisGeneration })

    const handlers = createHandlers(thisGeneration)

    try {
      await executeStream(
        { chatId: retryChatId, content, userMessageId, assistantMessageId, retryable: true },
        thisGeneration,
        handlers,
      )
    } catch(error) {
      // executeStream's finally block already handled machine events and cleanup.
      const errorMessage = getStreamErrorDisplayMessage(error, defaultErrorMessage, networkErrorMessage)
      trackStreamError(retryChatId, isNetworkError(error) ? 'network' : 'unknown')
      showSnackbar(errorMessage, 'critical', {
        heading: intl.formatMessage({
          id: 'nDA42QsZVeANpf0KNZxr',
          defaultMessage: 'Message failed',
          description: 'Snackbar heading when sending a chat message fails',
        }),
      })
      throw error
    }
  }, [
    latestGenerationRef,
    send,
    createHandlers,
    executeStream,
    defaultErrorMessage,
    networkErrorMessage,
    intl,
  ])

  // ── Stop ──

  const handleStopStreaming = useCallback(async() => {
    const hadActiveStream = !!streamAbortControllerRef.current || isStreaming
    if (!hadActiveStream) return false

    // Try to stop on server (best effort, don't block on failure)
    if (chatId !== undefined) {
      try {
        await stopMessageStream({ chatId })
      } catch {
        // Server stop failed, but we still abort locally
      }
    }

    // Always abort locally regardless of server result
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort()
      streamAbortControllerRef.current = null
    }

    currentStreamingGenerationRef.current = 0
    // STOP must arrive at the machine before STREAM_END from executeStream's finally block.
    // This ordering is guaranteed because abort() is synchronous, so the fetch rejection
    // that unwinds executeStream is scheduled as a microtask — STOP is dispatched here
    // synchronously before that microtask runs.
    send({ type: 'STOP' })
    streamingMessageIdRef.current = null

    if (chatId !== undefined) {
      trackStreamComplete(chatId, true)
    }

    return true
  }, [chatId, isStreaming, send, streamingMessageIdRef])

  // ── Reset ──

  const resetStreamingState = useCallback(() => {
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort()
      streamAbortControllerRef.current = null
    }
    streamingMessageIdRef.current = null
    send({ type: 'RESET' })
  }, [streamingMessageIdRef, send])

  return { handleSubmit, handleRegenerateResponse, handleStopStreaming, handleRetry, resetStreamingState }
}
